import * as THREE from "three";
import debug from "debug";
const log = debug("app:walls");

/**
 * Class to generate and trace lines on a grid based on wall data.
 * @category Experience
 * @subcategory GeometryManagement
 */
class MakeLines {
  /**
   * Constructs the MakeLines instance.
   * @param {Object} experience - The main application experience object.
   */
  constructor(experience) {
    this.experience = experience;
    this.outerLines = [];
  }

  /**
   * Initializes the line tracing process.
   * @param {Object} gridData - The grid data containing the processed grid and its origin.
   * @param {Array} gridData.oGrid - The processed outer grid with marked edges.
   * @param {THREE.Vector3} gridData.gridOrigin - The origin of the grid in the 3D scene.
   * @returns {Array} - An array of traced lines represented as segments.
   */
  init(gridData) {
    log("grid data in lines", gridData);
    this.outerLines = this.traceLines(gridData.oGrid, gridData.gridOrigin);
    return this.outerLines;
  }

  cleanup() {
    log("Cleaning up MakeLines instance");

    // Clear stored line data
    this.outerLines = [];

    // Remove references to prevent memory leaks
    this.experience = null;

    log("MakeLines cleanup completed.");
  }

  /**
   * Traces lines from the grid data based on the marked edges.
   * @param {Array} grid - The 2D grid array with marked edges (`'o'` for edge points).
   * @param {THREE.Vector3} gridOrigin - The origin of the grid in the 3D scene.
   * @returns {Array} - An array of traced lines represented as segments.
   */
  traceLines(grid, gridOrigin) {
    const gridCopy = grid.map((row) => [...row]);
    log("Original Grid Copy:", gridCopy);

    this.outerLines = [];

    // Iterate through the grid to find marked cells ('o')
    for (let z = 0; z < grid.length; z++) {
      for (let x = 0; x < grid[z].length; x++) {
        if (grid[z][x] === "o") {
          const segment = this.traceSegment(grid, x, z, gridOrigin);
          if (segment) {
            this.outerLines.push(...segment);
          }
        }
      }
    }

    log("outer lines", this.outerLines);
    return this.outerLines;
  }

  /**
   * Traces a single segment starting from a given cell in the grid.
   * @param {Array} grid - The 2D grid array with marked edges.
   * @param {number} startX - The starting x-coordinate in the grid.
   * @param {number} startZ - The starting z-coordinate in the grid.
   * @param {THREE.Vector3} gridOrigin - The origin of the grid in the 3D scene.
   * @returns {Array|null} - An array of segment objects or null if no segments were found.
   */
  traceSegment(grid, startX, startZ, gridOrigin) {
    const segmentLines = [];
    let x = startX;
    let z = startZ;
    let length = 0; // Start length at 0
    let angle = null;
    let wallX = 0,
      wallZ = 0;

    const directions = [
      { dx: -1, dz: 0, angle: 180, wx: 1, wz: 1 }, // x- (left)
      { dx: 0, dz: -1, angle: 90, wx: 0, wz: 1 }, // z- (up)
      { dx: 1, dz: 0, angle: 0, wx: 0, wz: 0 }, // x+ (right)
      { dx: 0, dz: 1, angle: 270, wx: 1, wz: 0 }, // z+ (down)
    ];

    while (true) {
      log;
      grid[z][x] = "+"; // Mark as processed
      let foundNextSegment = false;

      for (const { dx, dz, angle: dirAngle, wx, wz } of directions) {
        const nextX = x + dx;
        const nextZ = z + dz;

        // Check if the next cell is a valid segment continuation
        if (grid[nextZ] && grid[nextZ][nextX] === "o") {
          const foundNextCell = true;
          // set angle for the new segment
          if (angle === null) {
            angle = dirAngle;
          }

          /*           log(
            `line before: length=${length}, angle=${angle}, x=${x}, z=${z}, wallX=${wallX}, wallZ=${wallZ}`
          ); */
          // next cell is in the same direction like the current one
          if (angle === dirAngle && foundNextCell) {
            x = nextX;
            z = nextZ;
            length += 1;
            wallX = startX + wx;
            wallZ = startZ + wz;
            foundNextSegment = true;
            /*       log(
              `line after: length=${length}, angle=${angle}, x=${x}, z=${z}, wallX=${wallX}, wallZ=${wallZ}`
            ); */
            break;
          }

          //
          else {
            segmentLines.push({
              s: new THREE.Vector3(
                gridOrigin.x + wallX,
                0,
                gridOrigin.z + wallZ
              ),
              length,
              angle,
            });

            // Move to the last 'o' cell but do not increase the length
            startX = x;
            startZ = z;
            length = 0;
            angle = dirAngle;
            foundNextSegment = true;
            break;
          }
        }
      }

      // If no further segments are found, finalize the current segment
      if (!foundNextSegment) {
        // Finalize the last segment, excluding the last cell
        length += 1;
        log("theoretically this is last cell in the segment", length);
        log(`segmentLines:`, segmentLines);
        segmentLines.push({
          s: new THREE.Vector3(gridOrigin.x + wallX, 0, gridOrigin.z + wallZ),
          length,
          angle,
        });
        break;
      }
    }

    return segmentLines.length > 0 ? segmentLines : null;
  }
}

export default MakeLines;
